import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Tag, Descriptions, Table, Spin, Empty, Modal, Select, Tooltip, Badge, message } from 'antd';
import { QrcodeOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { educationSessionService, trainingService } from '@iso/services';
import SessionQrModal from './SessionQrModal';
import AssessmentFormSelect from './AssessmentFormSelect';
import { SESSION_DRAFT, sessionStatusMeta, sessionTransitions } from './sessionStatus';

/**
 * One training session: its details, its QR code, who has registered, and what
 * each of them was marked.
 *
 * Lifted out of TrainingAssetDetail so the Sessions tab and the asset
 * drill-down show the same screen. Recording an outcome used to be reachable
 * only by going Assets -> open an asset -> find the session, which is why it
 * read as missing; there is now a second way in, and only one copy of it.
 *
 * Owns its own fetch rather than taking a session object, so either caller can
 * hand over an id and nothing more.
 */

/** Matches SessionQrModal — see the note there on why 8s. */
const ATTENDEE_POLL_MS = 8000;

const SessionDetailModal = ({ visible, sessionId, propertyId, onClose, onChanged }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [savingCompetency, setSavingCompetency] = useState(null);
  const [editingAssessment, setEditingAssessment] = useState(false);
  const [savingAssessment, setSavingAssessment] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  // The attendee whose paper is open for marking, and their marked answers.
  const [reviewing, setReviewing] = useState(null);
  const [review, setReview] = useState(null);
  const [loadingReview, setLoadingReview] = useState(false);

  const inFlight = useRef(false);

  // Whether THIS viewer may record an outcome. The screen is readable by any
  // property member; changing a competency is not. Sent by the server so the
  // control can be disabled rather than offered and refused.
  const canRecordOutcome = session ? session.can_record_outcome !== false : false;

  const load = useCallback(async (opts = {}) => {
    if (!sessionId || inFlight.current) return;
    inFlight.current = true;
    if (!opts.quiet) setLoading(true);
    try {
      const res = await trainingService.getSessionDetail(sessionId);
      if (res && res.session) {
        setSession(res.session);
        setLastSynced(moment());
      }
    } catch (e) {
      // A failed background refresh keeps whatever is already on screen; the
      // next tick recovers it. Only the first, visible load is worth a message.
      if (!opts.quiet) message.error('Failed to load session details');
    } finally {
      inFlight.current = false;
      if (!opts.quiet) setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!visible || !sessionId) {
      setSession(null);
      setEditingAssessment(false);
      setLastSynced(null);
      return undefined;
    }

    load();

    // Same reasoning as the QR modal: people register DURING the session, and
    // a trainer with this open is usually waiting for exactly that. Marking
    // competency against a stale roster is the bug this prevents.
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      load({ quiet: true });
    }, ATTENDEE_POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') load({ quiet: true });
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [visible, sessionId, load]);

  const openReview = async (attendee) => {
    setReviewing(attendee);
    setReview(null);
    setLoadingReview(true);
    try {
      const res = await educationSessionService.getAssessmentResult(attendee.trainee_id);
      // This one endpoint answers in a different envelope on each base branch:
      // staging replies {success: true}, master replies {code: '0000'}. Accept
      // either so this component is identical on both and does not silently
      // report "could not load" on whichever branch it was not written against.
      if (res && (res.success === true || res.code === '0000')) {
        setReview(res.review);
      } else {
        message.error(res.message || 'Could not load that assessment.');
        setReviewing(null);
      }
    } catch (e) {
      message.error('Could not load that assessment.');
      setReviewing(null);
    }
    setLoadingReview(false);
  };

  // Attach, change or clear this session's assessment.
  //
  // Editable until the first person sits it — the server freezes it after that,
  // because a submitted score belongs to the paper it was marked against.
  // formId === null is a deliberate clear ("inherit the asset's default"), not
  // a no-op, so it must reach the server rather than being guarded out here.
  const applyAssessment = async (formId) => {
    setSavingAssessment(true);
    try {
      const res = await educationSessionService.setAssessment(sessionId, formId);
      if (res.code !== '0000') {
        message.error(res.message || 'The assessment could not be saved.');
        return;
      }
      message.success(res.message);
      setSession((prev) => (prev ? {
        ...prev,
        assessment: res.assessment,
        assessment_form_id: res.assessment_form_id,
      } : prev));
      setEditingAssessment(false);
      onChanged && onChanged();
    } finally {
      setSavingAssessment(false);
    }
  };

  // Trainer override. Written straight to the one attendee, then reflected
  // locally so the row updates without reloading the whole session.
  const handleCompetencyChange = async (traineeId, status) => {
    setSavingCompetency(traineeId);
    try {
      const res = await educationSessionService.setCompetency(traineeId, status);
      if (res.code !== '0000') {
        message.error(res.message || 'The competency could not be updated.');
        return;
      }
      message.success('Competency updated');
      setSession((prev) => (prev ? {
        ...prev,
        attendees: (prev.attendees || []).map((a) => (
          a.trainee_id === traineeId
            ? { ...a, competency_status: status, competency_source: 'trainer' }
            : a
        )),
      } : prev));
      onChanged && onChanged();
    } finally {
      setSavingCompetency(null);
    }
  };

  // Mark everyone who has no verdict yet. The one-by-one control stays the
  // record of truth; this is for the common case of a class that all passed,
  // where clicking through thirty identical dropdowns is the whole friction.
  // Lifecycle. Kept beside the roster deliberately: activating is what makes
  // the QR live, and completing is what closes the list the trainer is looking
  // at, so both belong on the screen showing that list.
  const [changingStatus, setChangingStatus] = useState(false);

  const changeStatus = async (next) => {
    setChangingStatus(true);
    try {
      const res = await educationSessionService.setStatus(sessionId, next);
      if (!res || res.code !== '0000') {
        message.error((res && res.message) || 'The session status could not be updated.');
        return;
      }
      message.success(res.message);
      setSession((prev) => (prev ? { ...prev, ...(res.education_session || { status: next }) } : prev));
      // Activating is nearly always followed by "now show me the code" — so
      // show it, rather than making the trainer find the button.
      if (next === 'active') setQrOpen(true);
      onChanged && onChanged();
    } catch (e) {
      message.error('The session status could not be updated.');
    } finally {
      setChangingStatus(false);
    }
  };

  const [bulkSaving, setBulkSaving] = useState(false);
  const attendees = (session && session.attendees) || [];
  const unmarked = attendees.filter(a => !a.competency_status);

  const markAllRemaining = async (status) => {
    if (unmarked.length === 0) return;
    setBulkSaving(true);
    try {
      const results = await Promise.all(
        unmarked.map(a =>
          educationSessionService.setCompetency(a.trainee_id, status)
            .then(res => ({ id: a.trainee_id, ok: res && res.code === '0000' }))
            .catch(() => ({ id: a.trainee_id, ok: false }))
        )
      );
      const okIds = new Set(results.filter(r => r.ok).map(r => r.id));
      const failed = results.length - okIds.size;

      setSession((prev) => (prev ? {
        ...prev,
        attendees: (prev.attendees || []).map((a) => (
          okIds.has(a.trainee_id)
            ? { ...a, competency_status: status, competency_source: 'trainer' }
            : a
        )),
      } : prev));

      if (failed > 0) {
        // Named rather than swallowed: a partial write here means some people
        // are recorded and some are not, and the trainer has to know which.
        message.warning(`${okIds.size} updated, ${failed} could not be saved. Set those individually.`);
      } else {
        message.success(`${okIds.size} attendees marked`);
      }
      onChanged && onChanged();
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <>
      <Modal
        open={visible}
        title="Session Details"
        onCancel={onClose}
        footer={[
          ...(canRecordOutcome && session
            ? sessionTransitions(session.status).map((t) => (
                <Button
                  key={t.to}
                  type={t.primary ? 'primary' : 'default'}
                  loading={changingStatus}
                  onClick={() => changeStatus(t.to)}
                  style={t.primary ? { background: '#0B2A1B', borderColor: '#0B2A1B' } : undefined}
                >
                  {t.label}
                </Button>
              ))
            : []),
          <Button
            key="qr"
            icon={<QrcodeOutlined />}
            onClick={() => setQrOpen(true)}
            // A draft has no live registration, so its code would be a
            // decoration. Activate first.
            disabled={!sessionId || (session && session.status === SESSION_DRAFT)}
          >
            Show QR code
          </Button>,
          <Button key="close" onClick={onClose}>Close</Button>,
        ]}
        width={860}
        destroyOnClose
      >
        {loading || !session || !session.session_date ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : (
          <>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 20 }}>
              <Descriptions.Item label="Status" span={2}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Tag color={sessionStatusMeta(session.status).color}>
                    {sessionStatusMeta(session.status).label}
                  </Tag>
                  <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                    {sessionStatusMeta(session.status).hint}
                  </span>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Training">{session.asset_name || session.training_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Date">
                {session.session_date ? moment(session.session_date).format('DD/MM/YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Trainer">{session.trainer_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Location">{session.location_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Delivery Method">
                {session.delivery_method ? <Tag>{session.delivery_method.replace(/_/g, ' ')}</Tag> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Start / Finish">
                {(session.start_time || '—') + ' – ' + (session.finish_time || '—')}
              </Descriptions.Item>
              {session.equipment && (
                <Descriptions.Item label="Equipment" span={2}>{session.equipment}</Descriptions.Item>
              )}
              <Descriptions.Item label="Assessment for this session" span={2}>
                {editingAssessment ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <AssessmentFormSelect
                        propertyId={propertyId}
                        value={session.assessment_form_id || null}
                        onChange={applyAssessment}
                        disabled={savingAssessment}
                        placeholder="Choose an assessment form"
                      />
                    </div>
                    <Button size="small" onClick={() => setEditingAssessment(false)} disabled={savingAssessment}>
                      Cancel
                    </Button>
                  </div>
                ) : session.assessment && session.assessment.has_assessment ? (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600 }}>{session.assessment.form_name}</span>
                    <Tag color={session.assessment.scored ? 'green' : 'default'}>
                      {session.assessment.scored
                        ? `Marked · pass ${session.assessment.pass_mark}%`
                        : 'Not marked'}
                    </Tag>
                    <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                      {session.assessment.source === 'asset'
                        ? 'inherited from the training asset'
                        : 'set for this session'}
                    </span>
                    {/* Frozen only once somebody has actually sat it — before
                        that, attaching the wrong paper is an ordinary mistake
                        and should be an ordinary fix. */}
                    {canRecordOutcome && (
                      session.assessment.submitted_count > 0 ? (
                        <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                          · locked, {session.assessment.submitted_count} already sat it
                        </span>
                      ) : (
                        <Button size="small" type="link" style={{ padding: 0 }} onClick={() => setEditingAssessment(true)}>
                          Change
                        </Button>
                      )
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ color: '#8c8c8c' }}>
                      None — anyone scanning the QR code just registers their attendance.
                    </span>
                    {/* Same authority as recording a competency: attaching the
                        paper decides what everyone scanning the QR is asked. */}
                    {canRecordOutcome && (
                      <Button size="small" type="link" style={{ padding: 0 }} onClick={() => setEditingAssessment(true)}>
                        Add an assessment
                      </Button>
                    )}
                  </div>
                )}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <Badge status="success" />
              <h4 style={{ margin: 0 }}>Attendees ({attendees.length})</h4>
              <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                {lastSynced
                  ? `updates as people register · last checked ${lastSynced.format('HH:mm:ss')}`
                  : 'updates as people register'}
              </span>
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => load({ quiet: true })}
                title="Refresh now"
              />
              <div style={{ flex: 1 }} />
              {canRecordOutcome && unmarked.length > 0 && (
                <>
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                    {unmarked.length} not yet marked:
                  </span>
                  <Button
                    size="small"
                    loading={bulkSaving}
                    onClick={() => markAllRemaining('competent')}
                  >
                    Mark all competent
                  </Button>
                  <Button
                    size="small"
                    loading={bulkSaving}
                    onClick={() => markAllRemaining('not_yet_competent')}
                  >
                    Mark all not yet
                  </Button>
                </>
              )}
            </div>

            {attendees.length === 0 ? (
              <Empty description="Nobody has registered yet — share the QR code to start the roster." />
            ) : (
              <Table
                size="small"
                dataSource={attendees}
                rowKey="trainee_id"
                pagination={false}
                columns={[
                  {
                    // full_name / email are resolved by the API and already cover
                    // outside trainees (a visitor added to a session has no user
                    // row to join to, so user_name would be blank).
                    title: 'Person Name',
                    dataIndex: 'full_name',
                    key: 'full_name',
                    render: (n) => <span style={{ fontWeight: 600 }}>{n || '—'}</span>,
                  },
                  {
                    title: 'Type',
                    dataIndex: 'trainee_type',
                    key: 'trainee_type',
                    render: t => <Tag>{t === 'property_user' ? 'Staff' : 'External'}</Tag>,
                  },
                  {
                    title: 'Email',
                    dataIndex: 'email',
                    key: 'email',
                    render: e => e || '—',
                  },
                  {
                    title: 'Assessment',
                    key: 'assessment',
                    render: (_, r) => {
                      if (!r.assessment_submission_id) {
                        return <span style={{ color: '#bfbfbf' }}>Not sat</span>;
                      }
                      // Submitted against an unmarked form: there is a record but
                      // no score, and saying "0%" would be a lie.
                      if (r.assessment_percentage === null || r.assessment_percentage === undefined) {
                        return <Tag>Submitted</Tag>;
                      }
                      const passed = r.assessment_result === 'pass';
                      const tag = (
                        <Tooltip title={`Scored ${r.assessment_score} of ${r.assessment_max_score} on ${moment(r.assessment_submitted_at).format('DD/MM/YYYY HH:mm')}`}>
                          <Tag color={passed ? 'green' : 'red'}>
                            {r.assessment_percentage}% {passed ? 'Pass' : 'Fail'}
                          </Tag>
                        </Tooltip>
                      );

                      // A score records an outcome without explaining it. Only
                      // somebody who may act on it gets to open the paper —
                      // these are a named person's answers.
                      if (!canRecordOutcome) {
                        return tag;
                      }

                      // A Button, not an anchor: it goes nowhere, and a link
                      // with no href is unreachable by keyboard.
                      return (
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: 'auto', textAlign: 'left' }}
                          onClick={() => openReview(r)}
                        >
                          <span>
                            {tag}
                            <div style={{ fontSize: 11 }}>Review answers</div>
                          </span>
                        </Button>
                      );
                    },
                  },
                  {
                    title: 'Competency',
                    dataIndex: 'competency_status',
                    key: 'competency_status',
                    width: 210,
                    render: (status, r) => (
                      // Editable in place: the trainer having the final word is
                      // only true if changing it is one click from where the
                      // score is shown.
                      <div>
                        <Select
                          size="small"
                          style={{ width: 160 }}
                          value={status || undefined}
                          placeholder="Not set"
                          disabled={!canRecordOutcome}
                          loading={savingCompetency === r.trainee_id}
                          onChange={(v) => handleCompetencyChange(r.trainee_id, v)}
                          options={[
                            { value: 'competent', label: 'Competent' },
                            { value: 'not_yet_competent', label: 'Not Yet Competent' },
                          ]}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                          {r.competency_source === 'assessment'
                            ? 'auto from assessment'
                            : r.competency_source === 'trainer'
                            ? 'set by trainer'
                            : ''}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: 'Notes',
                    dataIndex: 'notes',
                    key: 'notes',
                    render: n => n || '—',
                  },
                ]}
              />
            )}
          </>
        )}
      </Modal>

      <SessionQrModal
        visible={qrOpen}
        sessionId={qrOpen ? sessionId : null}
        onClose={() => setQrOpen(false)}
      />

      <Modal
        title={reviewing ? `Assessment — ${reviewing.full_name || 'Attendee'}` : 'Assessment'}
        open={reviewing !== null}
        onCancel={() => { setReviewing(null); setReview(null); }}
        footer={null}
        width={720}
      >
        {loadingReview && <Spin />}

        {!loadingReview && review && (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Result">
                {review.scored ? (
                  <Tag color={review.result === 'pass' ? 'green' : 'red'}>
                    {review.percentage}% {review.result === 'pass' ? 'Pass' : 'Fail'}
                  </Tag>
                ) : (
                  <Tag>Submitted — not marked</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Pass mark">
                {review.pass_mark !== null ? `${review.pass_mark}%` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Score">
                {review.scored ? `${review.score} of ${review.max}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Submitted">
                {review.submitted_at ? moment(review.submitted_at).format('DD/MM/YYYY HH:mm') : '—'}
              </Descriptions.Item>
            </Descriptions>

            {/* The marking is recomputed against the CURRENT answer key, while
                the attendee's row keeps what was recorded when they sat it. The
                two disagreeing means somebody edited the key since — worth
                saying out loud rather than letting a trainer wonder which
                number to trust. */}
            {review.scored
              && review.recorded_percentage !== null
              && review.recorded_percentage !== review.percentage && (
              <div style={{ marginBottom: 12, color: '#d46b08' }}>
                Recorded at the time as {review.recorded_percentage}%
                {review.recorded_result ? ` (${review.recorded_result})` : ''}. The answer key has
                changed since, so the marking below is against the current one.
              </div>
            )}

            <Table
              size="small"
              rowKey="key"
              pagination={false}
              dataSource={review.questions || []}
              columns={[
                { title: 'Question', dataIndex: 'label', key: 'label' },
                {
                  title: 'Their answer',
                  dataIndex: 'answer',
                  key: 'answer',
                  render: (v) => (v === null || v === undefined
                    ? <span style={{ color: '#bfbfbf' }}>Not answered</span>
                    : v),
                },
                ...(review.includes_key
                  ? [{
                      title: 'Correct answer',
                      dataIndex: 'expected',
                      key: 'expected',
                      render: (v) => (Array.isArray(v) ? v.join(', ') : (v || '—')),
                    }]
                  : []),
                {
                  title: 'Marked',
                  dataIndex: 'correct',
                  key: 'correct',
                  width: 110,
                  // Null is a question that carried no marks — a name, a
                  // comment. Neither right nor wrong, and colouring it either
                  // would imply it was graded.
                  render: (correct, row) => {
                    if (correct === null || correct === undefined) {
                      return <span style={{ color: '#bfbfbf' }}>Not marked</span>;
                    }
                    return (
                      <Tag color={correct ? 'green' : 'red'}>
                        {correct ? 'Correct' : 'Incorrect'}
                        {row.points ? ` (${row.awarded}/${row.points})` : ''}
                      </Tag>
                    );
                  },
                },
              ]}
            />

            {!review.includes_key && (
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 8 }}>
                The correct answers are visible to the form's author and to property
                owners and admins.
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default SessionDetailModal;
